import { useState } from 'react'
import { Rocket } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/apiClient'
import type { AppRecord, FormField } from '../types'
import { Modal } from './Modal'
import { RecordForm } from './RecordForm'

const conversionFields: FormField[] = [
  { key: 'name', label: 'Opportunity name', type: 'text', required: true, fullWidth: true },
  {
    key: 'customerId', label: 'Existing customer', type: 'lookup', lookupEndpoint: 'customers', lookupLabel: 'name', fullWidth: true,
    hint: 'Select a customer, or leave blank to create one from the lead.',
  },
  { key: 'customerName', label: 'New customer name', type: 'text' },
  {
    key: 'contactId', label: 'Existing contact', type: 'lookup', lookupEndpoint: 'contacts', lookupLabel: 'name',
    hint: 'Select a contact, or leave blank to create one from the lead.',
  },
  { key: 'contactName', label: 'New contact name', type: 'text' },
  { key: 'estimatedValue', label: 'Estimated value (€)', type: 'number' },
  { key: 'siteAddress', label: 'Site address', type: 'text', fullWidth: true },
  { key: 'siteCity', label: 'City', type: 'text' },
  { key: 'sitePostalCode', label: 'Postcode', type: 'text' },
]

export function LeadConversionButton({ lead, compact = false, label }: { lead: AppRecord; compact?: boolean; label?: string }) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const customerName = String(lead.name || '')
  const contactName = String(lead.contactName || lead.name || '')
  const isB2C = lead.customerType === 'B2C'
  const fields = isB2C ? conversionFields.filter((field) => field.key !== 'contactName') : conversionFields
  const buttonLabel = label ?? (lead.status === 'qualified' ? 'Convert lead' : 'Qualify & convert')

  const convert = async (values: Record<string, unknown>) => {
    const result = await api.action<{ lead: AppRecord; opportunity: AppRecord }>('leads', lead.id, 'convert', values)
    setOpen(false)
    navigate(`/opportunities/${result.opportunity.id}`)
  }

  return (
    <>
      <button
        className={compact
          ? 'inline-flex items-center gap-1.5 rounded-md bg-pine-50 px-2.5 py-2 text-xs font-bold text-pine-700 hover:bg-pine-100'
          : 'btn-primary'}
        onClick={() => setOpen(true)}
        title={buttonLabel}
      >
        <Rocket className="h-4 w-4" />
        <span>{compact ? (lead.status === 'qualified' ? 'Convert' : 'Qualify & convert') : buttonLabel}</span>
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Qualify and convert lead"
        description={isB2C
          ? 'For B2C leads, the customer and contact will be created with the same name, email and phone details.'
          : 'Link existing records or create the customer and contact from this lead. The resulting opportunity will retain the lead connection.'}
        size="lg"
      >
        <RecordForm
          fields={fields}
          initialValues={{
            name: `${customerName || contactName} opportunity`.trim(),
            customerName,
            contactName,
            estimatedValue: lead.estimatedNeed,
            siteCity: lead.city,
          }}
          submitLabel="Create opportunity"
          onSubmit={convert}
          onCancel={() => setOpen(false)}
        />
      </Modal>
    </>
  )
}
